import React, { useState } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet } from 'react-native';
import { useProfile, useTransactions } from '../hooks/useData';
import LoadingState from '../components/common/LoadingState';
import ErrorState from '../components/common/ErrorState';

const calculateActualSpending = (transactions, targetMonth = '2025-06') => {
  if (!transactions?.length) return {};
  
  const monthlyTransactions = transactions.filter(tx => 
    tx.date.startsWith(targetMonth) && tx.amount < 0 // Only expenses
  );
  
  const byCategory = {};
  monthlyTransactions.forEach(tx => {
    const category = tx.category.toLowerCase().replace(/\s+/g, '_');
    const amount = Math.abs(tx.amount);
    
    if (!byCategory[category]) {
      byCategory[category] = 0;
    }
    byCategory[category] += amount;
  });
  
  return byCategory;
};

const getStatus = (assigned, spent) => {
  const remaining = assigned - spent;
  const percentSpent = (spent / assigned) * 100;
  
  if (percentSpent >= 100) return 'Over Budget';
  if (percentSpent >= 90) return 'Nearly Spent';
  if (percentSpent >= 70) return 'On Track';
  if (percentSpent >= 50) return 'Funded';
  return 'Available';
};

const getStatusStyles = (status) => {
  switch (status) {
    case 'Over Budget': return { backgroundColor: '#FEE2E2', color: '#B91C1C' };
    case 'Nearly Spent': return { backgroundColor: '#FEF3C7', color: '#D97706' };
    case 'On Track': return { backgroundColor: '#DBEAFE', color: '#2563EB' };
    case 'Funded': return { backgroundColor: '#D1FAE5', color: '#059669' };
    case 'Available': return { backgroundColor: '#F3F4F6', color: '#374151' };
    default: return { backgroundColor: '#F3F4F6', color: '#374151' };
  }
};

const createBudgetGroups = (monthlySpending, actualSpending) => {
  const groups = [
    {
      name: 'Fixed Expenses',
      categories: [
        'rent',
        'auto_loan', 
        'student_loan',
        'utilities',
        'subscriptions',
        'phone',
        'internet'
      ]
    },
    {
      name: 'Variable Expenses',
      categories: [
        'groceries',
        'dining_out',
        'gas',
        'shopping',
        'entertainment',
        'travel'
      ]
    },
    {
      name: 'Savings & Investments',
      categories: [
        'savings',
        'investments'
      ]
    }
  ];

  return groups.map(group => ({
    ...group,
    categories: group.categories
      .filter(cat => monthlySpending[cat] !== undefined)
      .map(cat => {
        const assigned = monthlySpending[cat] || 0;
        const spent = actualSpending[cat] || 0;
        const remaining = assigned - spent;
        const status = getStatus(assigned, spent);
        
        return {
          name: cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          key: cat,
          assigned,
          activity: -spent,
          available: remaining,
          status
        };
      })
  }));
};

const Budget = () => {
  const { data: profile, loading: profileLoading, error: profileError, refresh: refreshProfile } = useProfile();
  const { data: transactions, loading: transactionsLoading, error: transactionsError, refresh: refreshTransactions } = useTransactions();
  
  const [expanded, setExpanded] = useState([true, true, true]);
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [currentMonth] = useState('June 2025');

  const loading = profileLoading || transactionsLoading;
  const error = profileError || transactionsError;
  const refresh = () => {
    refreshProfile();
    refreshTransactions();
  };

  if (loading) return <LoadingState message="Loading budget data..." />;
  if (error) return <ErrorState message={error} onRetry={refresh} />;
  if (!profile || !transactions) return null;

  const actualSpending = calculateActualSpending(transactions, '2025-06');
  const budgetGroups = createBudgetGroups(profile.monthly_spending, actualSpending);
  
  // Calculate totals
  const totalBudgeted = Object.values(profile.monthly_spending).reduce((sum, val) => sum + val, 0);
  const totalSpent = Object.values(actualSpending).reduce((sum, val) => sum + val, 0);
  const totalRemaining = totalBudgeted - totalSpent;

  const toggleGroup = (idx) => {
    setExpanded((prev) => prev.map((v, i) => (i === idx ? !v : v)));
  };

  const filteredGroups = budgetGroups.map(group => ({
    ...group,
    categories: group.categories.filter(cat => {
      if (selectedFilter === 'All') return true;
      if (selectedFilter === 'Over Budget') return cat.status === 'Over Budget';
      if (selectedFilter === 'Available') return cat.available > 0;
      if (selectedFilter === 'Nearly Spent') return cat.status === 'Nearly Spent';
      return true;
    })
  }));

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.monthNavigation}>
          <TouchableOpacity style={styles.navButton}>
            <Text style={styles.navButtonText}>‹</Text>
          </TouchableOpacity>
          <Text style={styles.monthTitle}>{currentMonth}</Text>
          <TouchableOpacity style={styles.navButton}>
            <Text style={styles.navButtonText}>›</Text>
          </TouchableOpacity>
        </View>
        <View style={[
          styles.remainingBadge,
          totalRemaining >= 0 ? styles.remainingPositive : styles.remainingNegative
        ]}>
          <Text style={[
            styles.remainingText,
            totalRemaining >= 0 ? styles.remainingTextPositive : styles.remainingTextNegative
          ]}>
            ${Math.abs(totalRemaining).toLocaleString()} {totalRemaining >= 0 ? 'Remaining' : 'Over Budget'}
          </Text>
        </View>
      </View>

      {/* Summary Cards */}
      <View style={styles.summaryGrid}>
        <View style={[styles.summaryCard, styles.budgetedCard]}>
          <Text style={styles.summaryLabel}>Total Budgeted</Text>
          <Text style={styles.budgetedAmount}>${totalBudgeted.toLocaleString()}</Text>
        </View>
        <View style={[styles.summaryCard, styles.spentCard]}>
          <Text style={styles.summaryLabel}>Total Spent</Text>
          <Text style={styles.spentAmount}>${totalSpent.toLocaleString()}</Text>
        </View>
        <View style={[
          styles.summaryCard,
          totalRemaining >= 0 ? styles.remainingCardPositive : styles.remainingCardNegative
        ]}>
          <Text style={[
            styles.summaryLabel,
            totalRemaining >= 0 ? styles.remainingLabelPositive : styles.remainingLabelNegative
          ]}>
            {totalRemaining >= 0 ? 'Remaining' : 'Over Budget'}
          </Text>
          <Text style={[
            styles.remainingAmount,
            totalRemaining >= 0 ? styles.remainingAmountPositive : styles.remainingAmountNegative
          ]}>
            {totalRemaining >= 0 ? '$' : '-$'}{Math.abs(totalRemaining).toLocaleString()}
          </Text>
        </View>
      </View>

      {/* Filters */}
      <View style={styles.filtersContainer}>
        {['All', 'Over Budget', 'Nearly Spent', 'Available'].map((filter) => (
          <TouchableOpacity
            key={filter}
            style={[
              styles.filterButton,
              selectedFilter === filter ? styles.filterButtonActive : styles.filterButtonInactive
            ]}
            onPress={() => setSelectedFilter(filter)}
          >
            <Text style={[
              styles.filterButtonText,
              selectedFilter === filter ? styles.filterButtonTextActive : styles.filterButtonTextInactive
            ]}>
              {filter}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Budget Table */}
      <View style={styles.tableContainer}>
        {/* Table Header */}
        <View style={styles.tableHeader}>
          <Text style={[styles.tableHeaderText, styles.categoryColumn]}>Category</Text>
          <Text style={[styles.tableHeaderText, styles.amountColumn]}>Budgeted</Text>
          <Text style={[styles.tableHeaderText, styles.amountColumn]}>Spent</Text>
          <Text style={[styles.tableHeaderText, styles.amountColumn]}>Remaining</Text>
          <Text style={[styles.tableHeaderText, styles.statusColumn]}>Status</Text>
        </View>

        {/* Table Body */}
        {filteredGroups.map((group, idx) => (
          <View key={group.name}>
            {/* Group Header */}
            <TouchableOpacity style={styles.groupHeader} onPress={() => toggleGroup(idx)}>
              <Text style={styles.groupHeaderText}>
                <Text style={styles.expandIcon}>{expanded[idx] ? '▼' : '▶'} </Text>
                {group.name}
              </Text>
            </TouchableOpacity>
            
            {/* Group Categories */}
            {expanded[idx] && group.categories.map((cat) => (
              <View key={cat.key} style={styles.tableRow}>
                <View style={styles.categoryColumn}>
                  <Text style={styles.categoryName}>{cat.name}</Text>
                </View>
                <View style={styles.amountColumn}>
                  <Text style={styles.budgetedText}>${cat.assigned.toLocaleString()}</Text>
                </View>
                <View style={styles.amountColumn}>
                  <Text style={styles.spentText}>${Math.abs(cat.activity).toLocaleString()}</Text>
                </View>
                <View style={styles.amountColumn}>
                  <Text style={[
                    styles.remainingText,
                    cat.available < 0 ? styles.negativeAmount : 
                    cat.available < cat.assigned * 0.1 ? styles.warningAmount : styles.positiveAmount
                  ]}>
                    {cat.available < 0 ? '-' : ''}${Math.abs(cat.available).toLocaleString()}
                  </Text>
                </View>
                <View style={styles.statusColumn}>
                  <View style={[styles.statusBadge, getStatusStyles(cat.status)]}>
                    <Text style={[styles.statusText, { color: getStatusStyles(cat.status).color }]}>
                      {cat.status}
                    </Text>
                  </View>
                </View>
              </View>
            ))}
          </View>
        ))}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  content: {
    maxWidth: 1280, // max-w-5xl equivalent
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
    flexWrap: 'wrap',
    gap: 16,
  },
  monthNavigation: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  navButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    backgroundColor: '#F3F4F6',
  },
  navButtonText: {
    fontSize: 18,
    color: '#374151',
    fontWeight: '600',
  },
  monthTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  remainingBadge: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 12,
  },
  remainingPositive: {
    backgroundColor: '#D1FAE5',
  },
  remainingNegative: {
    backgroundColor: '#FEE2E2',
  },
  remainingText: {
    fontSize: 18,
    fontWeight: '600',
  },
  remainingTextPositive: {
    color: '#059669',
  },
  remainingTextNegative: {
    color: '#DC2626',
  },
  
  // Summary Cards
  summaryGrid: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 24,
    // Stack on mobile
    '@media (max-width: 768px)': {
      flexDirection: 'column',
    },
  },
  summaryCard: {
    flex: 1,
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 16,
    alignItems: 'center',
  },
  budgetedCard: {
    backgroundColor: '#EFF6FF',
  },
  spentCard: {
    backgroundColor: '#F9FAFB',
  },
  remainingCardPositive: {
    backgroundColor: '#F0FDF4',
  },
  remainingCardNegative: {
    backgroundColor: '#FEF2F2',
  },
  summaryLabel: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 4,
  },
  budgetedAmount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#2563EB',
  },
  spentAmount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#374151',
  },
  remainingAmount: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  remainingAmountPositive: {
    color: '#059669',
  },
  remainingAmountNegative: {
    color: '#DC2626',
  },
  remainingLabelPositive: {
    color: '#059669',
  },
  remainingLabelNegative: {
    color: '#DC2626',
  },
  
  // Filters
  filtersContainer: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  filterButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    borderWidth: 1,
  },
  filterButtonActive: {
    backgroundColor: '#2563EB',
    borderColor: '#2563EB',
  },
  filterButtonInactive: {
    backgroundColor: '#FFFFFF',
    borderColor: '#E5E7EB',
  },
  filterButtonText: {
    fontSize: 14,
    fontWeight: '500',
  },
  filterButtonTextActive: {
    color: '#FFFFFF',
  },
  filterButtonTextInactive: {
    color: '#374151',
  },
  
  // Table
  tableContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingVertical: 12,
    paddingHorizontal: 4,
  },
  tableHeaderText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
  },
  categoryColumn: {
    flex: 3,
    paddingHorizontal: 12,
    justifyContent: 'center',
  },
  amountColumn: {
    flex: 2,
    paddingHorizontal: 12,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  statusColumn: {
    flex: 2,
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  // Group Headers
  groupHeader: {
    backgroundColor: '#F9FAFB',
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  groupHeaderText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#374151',
  },
  expandIcon: {
    color: '#9CA3AF',
  },
  
  // Table Rows
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  categoryName: {
    fontSize: 14,
    color: '#111827',
  },
  budgetedText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
    textAlign: 'right',
  },
  spentText: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'right',
  },
  remainingText: {
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'right',
  },
  positiveAmount: {
    color: '#059669',
  },
  negativeAmount: {
    color: '#DC2626',
  },
  warningAmount: {
    color: '#D97706',
  },
  
  // Status Badges
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
  },
});

export default Budget; 